import React, { useState, useRef, useEffect } from 'react';
import { FaChevronUp, FaArrowUp, FaSearch } from 'react-icons/fa';
import { label } from 'constants/labels';
import ChatContainer from './ChatContainer';
import SearchContainer from './SearchContainer';
import { loadChat, chat, queryEntries, getEntry } from 'lib/api';
import { EntryModel, EntryPointer, EntryType, ChatMessage } from 'lib/models';
import Button, { ButtonVariant } from 'components/Button';
import { useAppContext } from 'lib/state/TextProvider';

function makeClientSideChatMsg(msg: string, isUser: boolean): ChatMessage {
  return {
    text: msg,
    role: isUser ? "user" : "assistant",
    createdAt: new Date()
  };
}

function ChatBar() {
  const { state } = useAppContext();
  const inputBoxRef = useRef<HTMLInputElement|null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [searchResults, setSearchResults] = useState<EntryModel[]>([]);
  const [isSearch, setIsSearch] = useState<boolean>(false);
  const isSearchRef = useRef<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);

  useEffect(() => {
    const loadPreviousChatMessages = async () => {
      if (state.userProfile) {
        const chatMessages = await loadChat(state.userProfile.rootChatUuid);
        setMessages(chatMessages);
      }
    };
    loadPreviousChatMessages();
  }, [state.userProfile, state.userProfile?.rootChatUuid])

  useEffect(() => {
    const handleKeyDown = (event: any) => {
      if (event.key === 'Escape') {
        const activeElement = document.activeElement;
        if (activeElement === inputBoxRef.current && isSearchRef.current) {
          setIsSearch(false);
          isSearchRef.current = false;
        } else if (activeElement instanceof HTMLElement) {
          activeElement.blur();
          event.preventDefault();
        }
      } else if (event.key === '?') {
        const activeTag = document.activeElement?.tagName;
        if (inputBoxRef.current && activeTag == "BODY") {
          setIsSearch(true);
          isSearchRef.current = true;
          inputBoxRef.current.focus();
          event.preventDefault();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => { window.removeEventListener('keydown', handleKeyDown); };
  }, []);


  const handleSubmitClick = async () => {
    if (!inputValue) {
      return;
    }

    setIsCollapsed(false);

    if (isSearch) {
      setSearchResults([])
      const queryResults = await queryEntries(inputValue);
      const entries = await Promise.all(queryResults.map((x: EntryPointer) => {
        return getEntry(x.entryUuid, x.entryType);
      }));
      const filteredEntries = entries.filter((x: EntryModel) => {
        if (x.data.type !== EntryType.TEXT) {
          return false;
        }
        if (!x.data.text || x.data.text === "") {
          return false;
        }
        return true;
      });
      setSearchResults([...filteredEntries])
    } else {
      const userMsg = inputValue;
      setInputValue('');

      const currMessages = [...messages, makeClientSideChatMsg(userMsg, true)];
      setMessages(currMessages);

      if (state.userProfile) {
        let accumulatedMsg = "";
        for await (const msgChunk of chat(state.userProfile.rootChatUuid, userMsg)) {
          accumulatedMsg += msgChunk;
          setMessages([...currMessages, makeClientSideChatMsg(accumulatedMsg, false)]);
        }
      }
    }
  };

  const handleOnInputValueChange = (event: any) => {
    setInputValue(event.target.value);
    setIsCollapsed(false);
  };

  const handleFormSubmit = (event: any) => {
    event.preventDefault();
    handleSubmitClick();
  };

  const handleToggleSearchClick = () => {
    setIsCollapsed(true);
    setSearchResults([]);
    setIsSearch(!isSearch);
    isSearchRef.current = !isSearch;
  };

  return (
      <div className="z-10 bg-palette-5 p-4 rounded-3xl space-y-2 h-full">
        {isSearch ?
          <SearchContainer
            results={searchResults}
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />
          :
          <ChatContainer
            messages={messages}
            isCollapsed={isCollapsed}
            setIsCollapsed={setIsCollapsed}
          />
        }
        <div className="flex flex-col space-y-4">
            <div className="flex flex-row items-center">
              <form
                className="w-full overflow-x-hidden"
                onSubmit={handleFormSubmit}
              >
                <input
                  type="text"
                  ref={inputBoxRef}
                  className="w-full bg-palette-5 focus:outline-none text-palette-1 placeholder-palette-4 resize-none py-2"
                  placeholder={isSearch ? label('input_instructions_search') : label('input_instructions_placeholder')}
                  value={inputValue}
                  onChange={handleOnInputValueChange}
                />
              </form>
              {isCollapsed ? (
                  <Button
                    variant={ButtonVariant.FlatNoFill}
                    icon={<FaChevronUp />}
                    onClick={() => setIsCollapsed(false)}
                    size="sm"
                  />
              ) : null}
            </div>
            <div className="w-full flex flex-row justify-between items-center">
              <Button
                variant={ButtonVariant.FlatNoFill}
                icon={<FaSearch />}
                onClick={handleToggleSearchClick}
                selected={isSearch}
                size="sm"
              />
              <Button
                variant={ButtonVariant.Flat}
                icon={<FaArrowUp/>}
                disabled={!inputValue}
                onClick={handleSubmitClick}
              />
            </div>
        </div>
      </div>
  );
}

export default ChatBar;
